import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import * as PIXI from 'pixi.js';
import { Spine } from '@esotericsoftware/spine-pixi-v8';

const CONFIG = {
  SPINE_PATHS: {
    JSON: '/spin/spine/Monitor_Large.json',
    ATLAS: '/spin/spine/Monitor_Large.atlas',
    TEXTURE: '/spin/spine/Monitor_Large.png',
  },
  APP: {
    RESOLUTION: 1,
    AUTO_DENSITY: false,
  },
  SKELETON: {
    DEFAULT_WIDTH: 928,
    DEFAULT_HEIGHT: 1059.06,
    OFFSET_X: -464,
    OFFSET_Y: -60,
    INIT_DELAY: 100,
  },
  ANIM: {
    IDLE: '1',
    SPIN: 'animation3',
    NO_TICKETS: 'animation4',
  },
};

const setupSpinePosition = (spine, screenWidth, screenHeight) => {
  const { DEFAULT_WIDTH, DEFAULT_HEIGHT, OFFSET_X, OFFSET_Y } = CONFIG.SKELETON;
  const skeletonInfo = spine.skeleton.data.skeleton || {};
  const width  = skeletonInfo.width  || DEFAULT_WIDTH;
  const height = skeletonInfo.height || DEFAULT_HEIGHT;
  const offsetX = skeletonInfo.x != null ? skeletonInfo.x : OFFSET_X;
  const offsetY = skeletonInfo.y != null ? skeletonInfo.y : OFFSET_Y;

  // On mobile with notch the entire app-shell content is pushed down by
  // var(--tg-safe-top), so we shift the spine by the same amount to keep
  // the monitor aligned with the page content. Zero on desktop / no-notch.
  const safeTop = window?.Telegram?.WebApp?.safeAreaInset?.top ?? 0;

  const FIXED_WIDTH = 468;
  const TOP_ANCHOR = 128 + safeTop;
  const scale = FIXED_WIDTH / width;
  spine.scale.set(scale);

  const centerX = offsetX + width / 2 + 5;
  spine.x = screenWidth / 2 - centerX * scale;
  spine.y = TOP_ANCHOR + (offsetY + height) * scale;
};

/**
 * SpinBackground
 *
 * @param {boolean} isPlaying - reserved for future track-1 spin animation
 * @param {boolean} smileVisible - when true plays idle/smile animation; false fades it out
 * @param {function} onCycleComplete - called after the spin animation completes one cycle
 *
 * Imperative API (via ref):
 *   playNoTickets() — plays the no-tickets animation once, then returns to idle
 */
const SpinBackground = forwardRef(({ isPlaying = false, smileVisible = true, onCycleComplete }, ref) => {
  const canvasContainerRef = useRef(null);
  const appRef   = useRef(null);
  const spineRef = useRef(null);
  const resizeHandlerRef = useRef(null);

  // Init PIXI + Spine once
  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container) return;

    let app = null;
    let isMounted = true;

    const init = async () => {
      try {
        const screenWidth  = window.innerWidth;
        const screenHeight = window.innerHeight;

        app = new PIXI.Application();
        await app.init({
          width: screenWidth,
          height: screenHeight,
          backgroundAlpha: 0,
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
        });

        if (!isMounted || !canvasContainerRef.current) { app.destroy(); return; }

        canvasContainerRef.current.appendChild(app.canvas);
        appRef.current = app;

        app.canvas.style.position = 'absolute';
        app.canvas.style.top    = '0';
        app.canvas.style.left   = '0';
        app.canvas.style.width  = '100%';
        app.canvas.style.height = '100%';

        await PIXI.Assets.load([
          CONFIG.SPINE_PATHS.JSON,
          CONFIG.SPINE_PATHS.ATLAS,
          CONFIG.SPINE_PATHS.TEXTURE,
        ]);

        if (!isMounted) return;

        const spine = Spine.from({
          skeleton: CONFIG.SPINE_PATHS.JSON,
          atlas:    CONFIG.SPINE_PATHS.ATLAS,
          autoUpdate: true,
        });
        spineRef.current = spine;

        // Start with idle animation
        spine.state.setAnimation(0, CONFIG.ANIM.IDLE, true);

        await new Promise(resolve => setTimeout(resolve, CONFIG.SKELETON.INIT_DELAY));
        if (!isMounted) return;

        setupSpinePosition(spine, app.screen.width, app.screen.height);
        app.stage.addChild(spine);

        resizeHandlerRef.current = () => {
          if (!app || !spine || !isMounted) return;
          const w = window.innerWidth, h = window.innerHeight;
          app.renderer.resize(w, h);
          setTimeout(() => setupSpinePosition(spine, w, h), 50);
        };
        window.addEventListener('resize', resizeHandlerRef.current);
      } catch (err) {
        console.error('SpinBackground init error:', err);
      }
    };

    init();

    return () => {
      isMounted = false;
      if (resizeHandlerRef.current) window.removeEventListener('resize', resizeHandlerRef.current);
      if (appRef.current) {
        try { appRef.current.destroy(true, { children: true, texture: true, baseTexture: true }); }
        catch (e) { /* ignore */ }
        appRef.current = null;
      }
    };
  }, []);

  // Imperative API
  useImperativeHandle(ref, () => ({
    playNoTickets: () => {
      const spine = spineRef.current;
      if (!spine) return;
      const SMILE_SLOTS = ['mouth_blu', 'mouth_blu2', 'eyes_blu'];
      const setSmileAlpha = (alpha) => {
        SMILE_SLOTS.forEach(name => {
          const slot = spine.skeleton.findSlot(name);
          if (slot) slot.color.a = alpha;
        });
      };
      setSmileAlpha(1);
      spine.state.clearListeners();
      spine.state.setAnimation(0, CONFIG.ANIM.NO_TICKETS, false);
      spine.state.addListener({
        complete: (entry) => {
          if (entry.animation?.name === CONFIG.ANIM.NO_TICKETS) {
            spine.state.clearListeners();
            spine.state.setAnimation(0, CONFIG.ANIM.IDLE, true);
          }
        },
      });
    },
  }), []);

  // Control animation: smile (track 0) visible/hidden
  // isPlaying is reserved for a future track-1 spin animation
  useEffect(() => {
    const spine = spineRef.current;
    if (!spine) return;

    spine.state.clearListeners();

    const SMILE_SLOTS = ['mouth_blu', 'mouth_blu2', 'eyes_blu'];
    const setSmileAlpha = (alpha) => {
      SMILE_SLOTS.forEach(name => {
        const slot = spine.skeleton.findSlot(name);
        if (slot) slot.color.a = alpha;
      });
    };

    if (smileVisible) {
      setSmileAlpha(1);
      spine.state.setAnimation(0, CONFIG.ANIM.IDLE, true);
    } else {
      spine.state.setEmptyAnimation(0, 0.3);
      setTimeout(() => {
        if (spineRef.current) setSmileAlpha(0);
      }, 300);
    }
  }, [smileVisible]);

  return (
    <div className={"sb-backdrop"} style={styles.wrapper}>
      <div style={{ ...styles.glow, ...styles.glowTopRight }} />
      <div style={{ ...styles.glow, ...styles.glowMidLeft }} />
      <div style={{ ...styles.glow, ...styles.glowMidRight }} />
      <div style={{ ...styles.glow, ...styles.glowBottomLeft }} />
      <div style={styles.centerLight} />
      <div style={styles.bottomShadow} />
      <div ref={canvasContainerRef} style={styles.canvas} />
    </div>
  );
});

const styles = {
  wrapper: {
    // Fixed to the viewport: the Balance bar now lives in AppLayout above
    // .ls-page-wrapper, so the decorative background must span the whole
    // screen (incl. behind the balance bar) to stay full-bleed.
    position: 'fixed', top: 0, left: 0,
    width: '100%', height: '100vh',
    zIndex: -1, overflow: 'hidden', pointerEvents: 'none'
  },
  glow: {
    position: 'absolute', width: '55vw', height: '25vh',
    borderRadius: '50%', filter: 'blur(60px)', pointerEvents: 'none',
  },
  glowTopRight: {
    top: '8%', right: '-10%',
    background: 'radial-gradient(ellipse, rgba(104,247,255,0.35) 0%, rgba(104,247,255,0) 70%)',
  },
  glowMidLeft: {
    top: '25%', left: '-15%',
    background: 'radial-gradient(ellipse, rgba(75,52,128,0.7) 0%, rgba(75,52,128,0) 70%)',
  },
  glowMidRight: {
    top: '50%', right: '-15%',
    background: 'radial-gradient(ellipse, rgba(104,247,255,0.2) 0%, rgba(104,247,255,0) 70%)',
  },
  glowBottomLeft: {
    top: '68%', left: '-15%',
    background: 'radial-gradient(ellipse, rgba(75,52,128,0.5) 0%, rgba(75,52,128,0) 70%)',
  },
  centerLight: {
    position: 'absolute', top: '25%', left: '50%', transform: 'translateX(-50%)',
    width: '100vw', height: '45vh', borderRadius: '50%',
    background: 'radial-gradient(ellipse at center, rgba(104,247,255,0.12) 0%, rgba(14,8,20,0) 65%)',
    filter: 'blur(20px)', pointerEvents: 'none',
  },
  bottomShadow: {
    position: 'absolute', bottom: 0, left: 0,
    width: '100%', height: '22%', zIndex: 1, pointerEvents: 'none',
    background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)',
  },
  canvas: { position: 'absolute', inset: 0, width: '100%', height: '100%' },
};

export default SpinBackground;
